import { useState, useCallback, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import apiService, { leaguesAPI } from '../../../../services/apiService';

// Helper to get stored token (same as in apiService)
const getStoredToken = () => {
  try {
    return localStorage.getItem('fantasy_auth_token') || sessionStorage.getItem('fantasy_auth_token');
  } catch (e) {
    return null;
  }
};

// MODULE-LEVEL socket storage to survive component remounts
let globalSocket = null;
let globalLeagueId = null;
let globalDraftYear = null;

export const useDraftSocket = (leagueId, draftYear) => {
  const [socket, setSocket] = useState(globalSocket);
  const [connected, setConnected] = useState(globalSocket?.connected || false);
  const [connectionError, setConnectionError] = useState(null);
  const [connectionDetails, setConnectionDetails] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);

  const socketRef = useRef(globalSocket);
  const stateRequestedRef = useRef(false);

  // SSL error detection helper
  const isSSLError = useCallback((err) => {
    return err.message?.includes('CERT_AUTHORITY_INVALID') ||
           err.message?.includes('certificate') ||
           err.type === 'TransportError' ||
           err.message?.includes('ERR_CERT');
  }, []);

  // Connect to draft server
  const connect = useCallback(async () => {
    // Check global socket first - survives remounts
    if (globalSocket && globalLeagueId === leagueId && globalDraftYear === draftYear) {
      console.log('[useDraftSocket] Using existing global socket');
      socketRef.current = globalSocket;
      setSocket(globalSocket);
      setConnected(globalSocket.connected);
      return;
    }

    // If there's an existing socket for different league/year, disconnect it
    if (globalSocket) {
      console.log('[useDraftSocket] Disconnecting old socket for different league/year');
      globalSocket.disconnect();
      globalSocket = null;
    }

    // Don't connect if we don't have required parameters
    if (!leagueId || !draftYear) {
      console.warn('Cannot connect: missing leagueId or draftYear', { leagueId, draftYear });
      return;
    }

    console.log('[useDraftSocket] Creating new socket connection');
    setIsConnecting(true);
    setConnectionError(null);

    try {
      // Get connection details from API
      const response = await leaguesAPI.getLiveDraftConnection(leagueId);

      if (!response.success || !response.websocket_url) {
        throw new Error('Failed to get draft connection details');
      }

      setConnectionDetails(response);

      // Get a fresh token from the backend (cookie authenticates; response surfaces the JWT)
      let accessToken = null;
      try {
        const tokenRes = await apiService.get('/api/auth/token');
        accessToken = tokenRes?.data?.access_token || null;
      } catch (e) {
        console.warn('[useDraftSocket] Failed to fetch token from /api/auth/token, falling back to localStorage:', e);
        accessToken = getStoredToken();
      }

      if (!accessToken) {
        throw new Error('No authentication token available');
      }

      // Create socket connection
      const newSocket = io(response.websocket_url, {
        auth: { token: accessToken },
        transports: ['polling', 'websocket'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5,
        timeout: 20000
      });

      // Store response for use in error handler
      const websocketUrl = response.websocket_url;

      // Connection event handlers
      newSocket.on('connect', () => {
        console.log('[useDraftSocket] Socket connected');
        setConnected(true);
        setConnectionError(null);

        // Join draft room
        newSocket.emit('join-draft', {
          league_id: leagueId,
          year: draftYear
        });

        // Request initial draft state after joining (only once per connection)
        // Server should send it automatically, but request to be safe
        if (!stateRequestedRef.current) {
          stateRequestedRef.current = true;
          setTimeout(() => {
            if (newSocket.connected) {
              // Must pass league_id and year - server requires these
              newSocket.emit('get-draft-state', {
                league_id: leagueId,
                year: draftYear
              });
            }
          }, 500);
        }
      });

      newSocket.on('disconnect', (reason) => {
        console.log('[useDraftSocket] Socket disconnected:', reason);
        setConnected(false);
        // Reset state request flag on disconnect to allow re-request on reconnect
        stateRequestedRef.current = false;
      });

      newSocket.on('connect_error', (err) => {
        console.error('[useDraftSocket] Connection error:', err.message);
        setConnected(false);

        if (isSSLError(err)) {
          const baseUrl = websocketUrl
            .replace('wss://', 'https://')
            .replace('ws://', 'http://');
          setConnectionError(
            `SSL Certificate Error: The draft server's certificate is not trusted.\n` +
            `To fix this: Visit ${baseUrl} and accept the certificate.`
          );
        } else {
          setConnectionError(err.message);
        }
      });

      // Store in both ref and global
      socketRef.current = newSocket;
      globalSocket = newSocket;
      globalLeagueId = leagueId;
      globalDraftYear = draftYear;
      setSocket(newSocket);

    } catch (err) {
      setConnectionError(err.message);
    } finally {
      setIsConnecting(false);
    }
  }, [leagueId, draftYear, isSSLError]);

  // Disconnect - clears global socket too (for intentional disconnects)
  const disconnect = useCallback(() => {
    console.log('[useDraftSocket] Disconnect called');
    if (globalSocket) {
      globalSocket.disconnect();
      globalSocket = null;
      globalLeagueId = null;
      globalDraftYear = null;
    }
    socketRef.current = null;
    setSocket(null);
    setConnected(false);
  }, []);

  // Reconnect
  const reconnect = useCallback(async () => {
    disconnect();
    await new Promise(resolve => setTimeout(resolve, 500));
    await connect();
  }, [disconnect, connect]);

  // Emit helper
  const emit = useCallback((event, data) => {
    const activeSocket = globalSocket || socketRef.current;
    if (activeSocket?.connected) {
      activeSocket.emit(event, data);
    } else {
      console.warn('Cannot emit - socket not connected:', event);
    }
  }, []);

  // On/Off helpers
  const on = useCallback((event, callback) => {
    const activeSocket = globalSocket || socketRef.current;
    activeSocket?.on(event, callback);
  }, []);

  const off = useCallback((event, callback) => {
    const activeSocket = globalSocket || socketRef.current;
    activeSocket?.off(event, callback);
  }, []);

  // Sync state with global socket on mount (in case of remount)
  useEffect(() => {
    if (globalSocket) {
      // Check if socket is for the SAME league - if not, disconnect and clear it
      if (globalLeagueId !== leagueId || globalDraftYear !== draftYear) {
        console.log('[useDraftSocket] Clearing stale socket from different league/year');
        globalSocket.disconnect();
        globalSocket = null;
        globalLeagueId = null;
        globalDraftYear = null;
        // IMPORTANT: Also clear React state so component knows to reconnect
        socketRef.current = null;
        setSocket(null);
        setConnected(false);
        return; // Don't sync with a stale socket
      }
      console.log('[useDraftSocket] Syncing with existing global socket on mount');
      socketRef.current = globalSocket;
      setSocket(globalSocket);
      setConnected(globalSocket.connected);
    }
    // Note: We intentionally do NOT disconnect on unmount
    // The global socket survives remounts for better UX
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - run only on mount

  return {
    socket,
    connected,
    connectionError,
    connectionDetails,
    isConnecting,
    connect,
    disconnect,
    reconnect,
    emit,
    on,
    off
  };
};
